import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../../config/config';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SelectorIdioma from '../../components/ui/SelectorIdioma';
import { normalizarTelefonoInternacional, validarTelefonoInternacional } from '../../utils/telefono';
import {
  FiAtSign, FiLock, FiEye, FiEyeOff, FiUser, FiMail, FiPhone, FiGlobe,
  FiArrowLeft, FiCamera, FiCheckCircle, FiKey, FiArrowRight,
} from 'react-icons/fi';

// Recortar píxeles de un canvas a partir del área visible
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.85);
  });
};

const TOTAL_STEPS = 4;

// El rótulo de cada paso se traduce al pintarlo
const stepMeta = [
  { clave: 'registro.pasos.acceso', icon: FiKey },
  { clave: 'registro.pasos.datos', icon: FiUser },
  { clave: 'registro.pasos.contacto', icon: FiPhone },
  { clave: 'registro.pasos.foto', icon: FiCamera },
];

const EmpiezaAhora = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    usuario: '',
    contrasena: '',
    confirmarContrasena: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    email: '',
    webs_del_cliente: '',
  });
  const [fotoFile, setFotoFile] = useState(null);       // Blob recortado final
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ── Crop state ──────────────────────────────────────────────────────────────
  const [rawImageSrc, setRawImageSrc] = useState(null);  // data-URL de la img original
  const [previewUrl, setPreviewUrl] = useState(null);     // data-URL del recorte final
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  // ────────────────────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Abrir cropper al seleccionar archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    // Reset input para permitir reseleccionar el mismo archivo
    e.target.value = '';
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const aplicarRecorte = async () => {
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setFotoFile(blob);
      setShowCropper(false);
    } catch {
      toast.error(t('registro.errores.recorte'));
    }
  };

  const cancelarRecorte = () => {
    setShowCropper(false);
    setRawImageSrc(null);
  };

  const quitarFoto = () => {
    setPreviewUrl(null);
    setFotoFile(null);
    setRawImageSrc(null);
  };

  // ── Errores ─────────────────────────────────────────────────────────────────
  const showError = (message) => {
    toast.error(message, {
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // ── Validaciones ─────────────────────────────────────────────────────────────
  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      const usuarioRegex = /^[a-z0-9_]+$/;
      if (!usuarioRegex.test(formData.usuario)) {
        showError(t('registro.errores.usuarioFormato'));
        return false;
      }
      if (formData.usuario.length < 3) {
        showError(t('registro.errores.usuarioCorto'));
        return false;
      }
      if (formData.contrasena.length < 5) {
        showError(t('registro.errores.contrasenaCorta'));
        return false;
      }
      if (formData.contrasena !== formData.confirmarContrasena) {
        showError(t('registro.errores.contrasenasNoCoinciden'));
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.nombre) { showError(t('registro.errores.nombreVacio')); return false; }
      if (!formData.apellidos) { showError(t('registro.errores.apellidosVacio')); return false; }
    }
    if (currentStep === 3) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) { showError(t('registro.errores.emailInvalido')); return false; }
      if (!formData.telefono) { showError(t('registro.errores.telefonoVacio')); return false; }
      const telefono = validarTelefonoInternacional(formData.telefono, { obligatorio: true });
      if (!telefono.valido) {
        showError(t(telefono.error === 'formato' ? 'registro.errores.telefonoFormato' : 'registro.errores.telefonoInvalido'));
        return false;
      }
      setFormData((prev) => ({ ...prev, telefono: telefono.numero }));
      if (!formData.webs_del_cliente) { showError(t('registro.errores.websVacio')); return false; }
    }
    return true;
  };

  const changeStep = (newStep) => {
    if (newStep > step && !validateStep(step)) return;
    setStep(newStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Avanzar con Enter en el último input de cada paso
  const handleKeyDown = (e, isLastInput) => {
    if (e.key === 'Enter' && isLastInput) {
      e.preventDefault();
      if (step < TOTAL_STEPS) changeStep(step + 1);
    }
  };

  // ── Envío ────────────────────────────────────────────────────────────────────
  const notificarAdministrador = async () => {
    try {
      await axios.post(`${BASE_URL}/notificar-registro`, {
        usuario: formData.usuario,
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        webs_aseguradas: formData.webs_del_cliente,
      });
    } catch (error) {
      console.error('Error al enviar la notificación:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key] || '');
    });
    if (fotoFile) {
      formDataToSend.append('fotoPerfil', fotoFile, 'avatar.webp');
    }

    try {
      await axios.post(`${BASE_URL}/registrar-usuario`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
      await notificarAdministrador();
    } catch (err) {
      const msg = err.response?.data?.message || t('registro.errores.registro');
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="registro-pagina">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="bg-intro-blob bg-intro-blob-1"></div>
      <div className="inicio-idioma"><SelectorIdioma /></div>
      <Link to="/" className="volver-inicio"><FiArrowLeft /> {t('comun.volverInicio')}</Link>

      {/* Modal de recorte */}
      {showCropper && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h3>{t('registro.ajustarFoto')}</h3>
              <p>{t('registro.ajustarFotoTexto')}</p>
            </div>
            <div className="crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="crop-zoom-row">
              <span>{t('registro.zoom')}</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="crop-zoom-slider"
              />
            </div>
            <div className="crop-actions">
              <button type="button" className="crop-btn-cancel" onClick={cancelarRecorte}>{t('comun.cancelar')}</button>
              <button type="button" className="crop-btn-apply" onClick={aplicarRecorte}>{t('registro.aplicarRecorte')}</button>
            </div>
          </div>
        </div>
      )}

      {success ? (
        <div className="registro-card registro-success-card">
          <div className="registro-success-icon"><FiCheckCircle /></div>
          <h2>{t('registro.exitoTitulo')}</h2>
          <p>{t('registro.exitoTexto')}</p>
          <Link to="/iniciar-sesion" className="registro-success-btn">{t('registro.consultarEstado')}</Link>
        </div>
      ) : (
        <div className="registro-card">

          {/* Cabecera */}
          <div className="registro-header">
            <h1>{t('registro.titulo')}</h1>
            <p>{t('registro.subtitulo')}</p>
          </div>

          {/* Indicador de pasos */}
          <div className="step-indicator">
            {stepMeta.map(({ clave, icon: Icon }, i) => {
              const num = i + 1;
              const isCompleted = num < step;
              const isCurrent = num === step;
              return (
                <React.Fragment key={num}>
                  <div className={`step-dot-wrapper ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}>
                    <div className="step-dot">
                      {isCompleted ? <FiCheckCircle className="step-check" /> : <Icon />}
                    </div>
                    <span className="step-dot-label">{t(clave)}</span>
                  </div>
                  {i < stepMeta.length - 1 && (
                    <div className={`step-connector ${isCompleted ? 'filled' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>

            {/* ── PASO 1 ── */}
            {step === 1 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>{t('registro.paso1Titulo')}</h2>
                  <p>{t('registro.paso1Texto')}</p>
                </div>
                <div className="step-group">
                  <div className="campo-inicio">
                    <label htmlFor="usuario">{t('registro.nombreUsuario')}</label>
                    <span className="campo-hint">{t('registro.hintUsuario')}</span>
                    <div className="campo-inicio-input">
                      <FiAtSign className="campo-inicio-icon" />
                      <input
                        id="usuario"
                        type="text"
                        name="usuario"
                        placeholder={t('registro.placeholderUsuario')}
                        value={formData.usuario}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        required
                      />
                    </div>
                  </div>
                  <div className="campo-inicio">
                    <label htmlFor="contrasena">{t('comun.contrasena')}</label>
                    <div className="campo-inicio-input">
                      <FiLock className="campo-inicio-icon" />
                      <input
                        id="contrasena"
                        type={showPass ? 'text' : 'password'}
                        name="contrasena"
                        value={formData.contrasena}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        autoComplete="new-password"
                        placeholder={t('registro.placeholderContrasena')}
                        required
                      />
                      <button type="button" className="campo-inicio-toggle" onClick={() => setShowPass(v => !v)} tabIndex="-1" aria-label={showPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}>
                        {showPass ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  <div className="campo-inicio">
                    <label htmlFor="confirmarContrasena">{t('registro.confirmarContrasena')}</label>
                    <div className="campo-inicio-input">
                      <FiLock className="campo-inicio-icon" />
                      <input
                        id="confirmarContrasena"
                        type={showConfirmPass ? 'text' : 'password'}
                        name="confirmarContrasena"
                        value={formData.confirmarContrasena}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                        autoComplete="new-password"
                        placeholder={t('registro.placeholderConfirmar')}
                        required
                      />
                      <button type="button" className="campo-inicio-toggle" onClick={() => setShowConfirmPass(v => !v)} tabIndex="-1" aria-label={showConfirmPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}>
                        {showConfirmPass ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="grid-step-buttons single-right">
                  <button type="button" className="btn-step-next" onClick={() => changeStep(2)}>{t('registro.siguiente')} <FiArrowRight /></button>
                </div>
              </div>
            )}

            {/* ── PASO 2 ── */}
            {step === 2 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>{t('registro.paso2Titulo')}</h2>
                  <p>{t('registro.paso2Texto')}</p>
                </div>
                <div className="step-group step-group-cols">
                  <div className="campo-inicio">
                    <label htmlFor="nombre">{t('comun.nombre')}</label>
                    <div className="campo-inicio-input">
                      <FiUser className="campo-inicio-icon" />
                      <input
                        id="nombre"
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        placeholder={t('registro.placeholderNombre')}
                        required
                      />
                    </div>
                  </div>
                  <div className="campo-inicio">
                    <label htmlFor="apellidos">{t('comun.apellidos')}</label>
                    <div className="campo-inicio-input">
                      <FiUser className="campo-inicio-icon" />
                      <input
                        id="apellidos"
                        type="text"
                        name="apellidos"
                        value={formData.apellidos}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                        placeholder={t('registro.placeholderApellidos')}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(1)}><FiArrowLeft /> {t('registro.anterior')}</button>
                  <button type="button" className="btn-step-next" onClick={() => changeStep(3)}>{t('registro.siguiente')} <FiArrowRight /></button>
                </div>
              </div>
            )}

            {/* ── PASO 3 ── */}
            {step === 3 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>{t('registro.paso3Titulo')}</h2>
                  <p>{t('registro.paso3Texto')}</p>
                </div>
                <div className="step-group">
                  <div className="campo-inicio">
                    <label htmlFor="email">{t('comun.email')}</label>
                    <div className="campo-inicio-input">
                      <FiMail className="campo-inicio-icon" />
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        placeholder={t('registro.placeholderEmail')}
                        required
                      />
                    </div>
                  </div>
                  <div className="campo-inicio">
                    <label htmlFor="telefono">{t('comun.telefono')}</label>
                    <span className="campo-hint">{t('registro.hintTelefono')}</span>
                    <div className="campo-inicio-input">
                      <FiPhone className="campo-inicio-icon" />
                      <input
                        id="telefono"
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        onBlur={() => setFormData((prev) => ({ ...prev, telefono: normalizarTelefonoInternacional(prev.telefono) }))}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        placeholder={t('registro.placeholderTelefono')}
                        inputMode="tel"
                        required
                      />
                    </div>
                  </div>
                  <div className="campo-inicio">
                    <label htmlFor="webs_del_cliente">{t('registro.websMantenimiento')}</label>
                    <span className="campo-hint">{t('registro.hintWebs')}</span>
                    <div className="campo-inicio-input">
                      <FiGlobe className="campo-inicio-icon" />
                      <input
                        id="webs_del_cliente"
                        type="text"
                        name="webs_del_cliente"
                        value={formData.webs_del_cliente}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                        placeholder={t('registro.placeholderWebs')}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(2)}><FiArrowLeft /> {t('registro.anterior')}</button>
                  <button type="button" className="btn-step-next" onClick={() => changeStep(4)}>{t('registro.siguiente')} <FiArrowRight /></button>
                </div>
              </div>
            )}

            {/* ── PASO 4 ── */}
            {step === 4 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>{t('registro.paso4Titulo')} <span className="opcional">{t('registro.opcional')}</span></h2>
                  <p>{t('registro.paso4Texto')}</p>
                </div>

                <div className="foto-perfil-uploader">
                  {previewUrl ? (
                    <div className="foto-preview-wrap">
                      <img src={previewUrl} alt={t('registro.vistaPrevia')} className="foto-preview-circle" />
                      <div className="foto-preview-actions">
                        <button type="button" className="btn-foto-change" onClick={() => fileInputRef.current?.click()}>
                          {t('registro.cambiarFoto')}
                        </button>
                        <button type="button" className="btn-foto-remove" onClick={quitarFoto}>
                          {t('registro.quitar')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="foto-upload-placeholder" onClick={() => fileInputRef.current?.click()}>
                      <div className="foto-upload-icon"><FiCamera /></div>
                      <span className="foto-upload-text">{t('registro.subirFoto')}</span>
                      <span className="foto-upload-hint">{t('registro.formatosFoto')}</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(3)}><FiArrowLeft /> {t('registro.anterior')}</button>
                  <button type="submit" className="btn-step-submit" disabled={loading}>
                    {loading ? (
                      <span className="btn-loading">
                        <span className="btn-spinner" /> {t('registro.enviando')}
                      </span>
                    ) : t('registro.completar')}
                  </button>
                </div>
              </div>
            )}

          </form>
        </div>
      )}
    </main>
  );
};

export default EmpiezaAhora;
